
import React, { Suspense } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';

import { trackPageview } from '@cvt/tracking';
import { useScrollTop } from '@cvt/hooks/useScrollTop';
import { BodyLoading } from '@cvt/components/layout/BodyLoading';
import { useRouter } from '@cvt/routing';
import ErrorBoundary from '@cvt/components/ErrorBoundary';

import config from '@shared/config';
import { AuthContext } from '@modules/Auth/contexts';
import { Authenticated, NotAuthenticated } from '@modules/Auth/components';
import { UserContext } from '@modules/Users/contexts';
import { HubBodyLoading } from '@modules/Hub/components/HubBodyLoading';
import { usePrefetchHubProfile } from '@modules/Hub/hooks/useHubProfile';


const Login = React.lazy(() => import('@modules/Auth/views/Login').then((m) => ({ default: m.Login })));
const SignUp = React.lazy(() => import('@modules/Auth/views/SignUp').then((m) => ({ default: m.SignUp })));
const Impersonate = React.lazy(() => import('@modules/Auth/views/Impersonate').then((m) => ({ default: m.Impersonate })));
const ResetPassword = React.lazy(() => import('@modules/Auth/views/ResetPassword').then((m) => ({ default: m.ResetPassword })));

const Users = React.lazy(() => import('@modules/Users/views/Users').then((m) => ({ default: m.Users })));
const CreateUser = React.lazy(() => import('@modules/Users/views/CreateUser').then((m) => ({ default: m.CreateUser })));
const EditUser = React.lazy(() => import('@modules/Users/views/EditUser').then((m) => ({ default: m.EditUser })));
const Invite = React.lazy(() => import('@modules/Users/views/Invite').then((m) => ({ default: m.Invite })));
const Profile = React.lazy(() => import('@modules/Users/views/Profile').then((m) => ({ default: m.Profile })));

const Companies = React.lazy(() => import('@modules/Companies/views/Companies').then((m) => ({ default: m.Companies })));
const CreateCompany = React.lazy(() => import('@modules/Companies/views/CreateCompany').then((m) => ({ default: m.CreateCompany })));

const LeadFormPage = React.lazy(() => import('@modules/Leads/views/LeadFormPage').then((m) => ({ default: m.LeadFormPage })));
const Leads = React.lazy(() => import('@modules/Leads/views/Leads').then((m) => ({ default: m.Leads })));
const ImpersonateLead = React.lazy(() => import('@modules/Leads/views/ImpersonateLead').then((m) => ({ default: m.ImpersonateLead })));

const HubApp = React.lazy(() => import('@modules/Hub/HubApp'));
const HubLogin = React.lazy(() => import('@modules/Hub/views/HubLogin'));

const PartnerPortal = React.lazy(() => import('./PartnerPortal/PartnerPortal').then((m) => ({ default: m.PartnerPortal })));

const EditCompany = React.lazy(() => import('./Companies/EditCompany').then((m) => ({ default: m.EditCompany })));
const Board = React.lazy(() => import('./Projects/Board').then((m) => ({ default: m.Board })));
const PublicChecklist = React.lazy(() => import('./Projects/PublicChecklist').then((m) => ({ default: m.PublicChecklist })));
const ViewProject = React.lazy(() => import('./Projects/ViewProject'));


export const Root = () => {
  const router = useRouter();
  useScrollTop();
  const { loading, isLoggedIn } = React.useContext(AuthContext);
  const { isAdmin, isHomeowner, isPartner } = React.useContext(UserContext);
  const location = useLocation();

  const defaultAuthenticatedPath = isAdmin
    ? router.home.path
    : isPartner
      ? router.partnerPortal.path
      : router.homeownerHub.path;

  const unauthenticatedRedirect = location.pathname.startsWith(router.homeownerHub.path)
    ? router.homeownerHub.login.path
    : router.auth.login.path;

  React.useEffect(() => {
    trackPageview();
  }, [location]);

  const inHub = location.pathname.startsWith(router.homeownerHub.path);
  const loadingFallback = inHub ? <HubBodyLoading/> : <BodyLoading height="100vh"/>;

  usePrefetchHubProfile(isLoggedIn && inHub);

  if(loading) {
    return loadingFallback;
  }

  return (
    <ErrorBoundary>
      <Suspense fallback={loadingFallback}>
        <NotAuthenticated>
          <Routes>
            <Route path="/checklist/:generatedLinkSegment" element={<PublicChecklist/>}/>
            <Route path={router.leadForm.byPartner().path} element={<LeadFormPage/>}/>
            <Route path={router.auth.login.path} element={<Login/>}/>
            <Route path={router.auth.resetPassword.path} element={<ResetPassword/>}/>
            <Route path={router.auth.signup.path} element={config.featureFlags.signup ? <SignUp/> : <Navigate to={router.auth.login.path}/>}/>
            <Route path={router.auth.impersonateUser.path} element={<Impersonate/>}/>
            <Route path={router.users.invite().path} element={<Invite/>}/>
            <Route path={router.homeownerHub.login.path} element={<HubLogin/>}/>
            <Route path="*" element={<Navigate to={unauthenticatedRedirect}/>}/>
          </Routes>
        </NotAuthenticated>
        <Authenticated>
          <Routes>
            <Route path="/checklist/:generatedLinkSegment" element={<PublicChecklist/>}/>
            <Route path={router.leadForm.byPartner().path} element={<LeadFormPage/>}/>
            <Route path={router.home.path} element={isAdmin ? <Board/> : <Navigate to={defaultAuthenticatedPath} replace/>}/>
            <Route path={`${router.projects.view().path}/*`} element={<ViewProject/>}/>
            <Route path={router.auth.impersonateUser.path} element={<Impersonate/>}/>
            <Route path={router.users.myAccount.path} element={<Profile/>}/>
            <Route path={router.users.path} element={<Users/>}/>
            <Route path={router.users.create.path} element={<CreateUser/>}/>
            <Route path={router.users.edit().path} element={<EditUser/>}/>
            <Route path={router.companies.path} element={<Companies/>}/>
            <Route path={router.companies.create.path} element={<CreateCompany/>}/>
            <Route path={router.companies.edit().path} element={<EditCompany/>}/>
            <Route path={router.leads.path} element={<Leads/>}/>
            <Route
              path={router.leads.impersonate().path}
              element={isAdmin ? <ImpersonateLead/> : <Navigate to={defaultAuthenticatedPath} replace/>}
            />
            <Route
              path={`${router.homeownerHub.path}/*`}
              element={isHomeowner ? <HubApp/> : <Navigate to={defaultAuthenticatedPath} replace/>}
            />
            <Route
              path={router.partnerPortal.path}
              element={isPartner ? <PartnerPortal/> : <Navigate to={defaultAuthenticatedPath} replace/>}
            />
            <Route path="*" element={<Navigate to={defaultAuthenticatedPath}/>}/>
          </Routes>
        </Authenticated>
      </Suspense>
    </ErrorBoundary>
  );
};
